<div class="fm-preview-backdrop" (click)="onBackdropClick($event)">
  <div class="fm-preview-modal">
    <div class="fm-preview-header">
      <div class="fm-preview-header-title">
        <h3 class="fm-preview-heading">Storybook Preview</h3>
        <p class="fm-preview-subheading mb-0">{{ pageRangeLabel() }}</p>
      </div>

      <div class="fm-preview-toggle">
        <button
          type="button"
          class="fm-preview-toggle-btn"
          [class.active]="viewMode() === 'book'"
          (click)="setView('book')"
        >
          <img src="images/icons/fm-book-icon.svg" class="fm-svg" alt="" /> Book View
        </button>
        <button
          type="button"
          class="fm-preview-toggle-btn"
          [class.active]="viewMode() === 'scroll'"
          (click)="setView('scroll')"
        >
          <img src="images/icons/fm-list.svg" class="fm-svg" alt="" /> Scroll View
        </button>
      </div>

      <button type="button" class="fm-preview-close" (click)="close()" aria-label="Close preview">
        &#x2715;
      </button>
    </div>

    <div class="fm-preview-body" [class.fm-preview-body-scroll]="viewMode() === 'scroll'">
      @if (totalPages() === 0) {
        <div class="fm-preview-empty">
          <p class="mb-0">This storybook doesn't have any generated pages yet.</p>
        </div>
      } @else if (viewMode() === 'book') {
        <div class="fm-preview-book-area">
          <div class="fm-preview-spread-row">
            <button
              type="button"
              class="fm-preview-nav"
              [disabled]="!canGoPrev()"
              (click)="goPrev()"
              aria-label="Previous page"
            >
              &#8592;
            </button>

            <div class="fm-book-spread">
              <div class="fm-book-page fm-book-page-left">
                @if (leftPage(); as page) {
                  <ng-container
                    [ngTemplateOutlet]="pageContent"
                    [ngTemplateOutletContext]="{ $implicit: page, index: currentSpreadIndex() }"
                  ></ng-container>
                }
              </div>
              <div class="fm-book-page fm-book-page-right">
                @if (rightPage(); as page) {
                  <ng-container
                    [ngTemplateOutlet]="pageContent"
                    [ngTemplateOutletContext]="{ $implicit: page, index: currentSpreadIndex() + 1 }"
                  ></ng-container>
                }
              </div>
            </div>

            <button
              type="button"
              class="fm-preview-nav"
              [disabled]="!canGoNext()"
              (click)="goNext()"
              aria-label="Next page"
            >
              &#8594;
            </button>
          </div>

          <div class="fm-preview-pagination">
            <button
              type="button"
              class="fm-preview-page-btn fm-preview-page-arrow"
              [disabled]="!canGoPrev()"
              (click)="goPrev()"
              aria-label="Previous page"
            >
              &#8249;
            </button>
            @for (item of pageNumbers(); track $index) {
              @if (item === '…') {
                <span class="fm-preview-page-ellipsis">&hellip;</span>
              } @else {
                <button
                  type="button"
                  class="fm-preview-page-btn"
                  [class.active]="item === activePageNumber()"
                  (click)="goToPage(item)"
                >
                  {{ item }}
                </button>
              }
            }
            <button
              type="button"
              class="fm-preview-page-btn fm-preview-page-arrow"
              [disabled]="!canGoNext()"
              (click)="goNext()"
              aria-label="Next page"
            >
              &#8250;
            </button>
          </div>
        </div>
      } @else {
        <!-- Fixed footer layout: the page list scrolls in its own region;
             the pagination bar is a separate sibling below it, so it's
             always visible from page 1 — not scrolled away with the list. -->
        <div class="fm-preview-book-area fm-preview-book-area-scroll">
          <div class="fm-preview-scroll-area">
            @for (page of pages(); track page.pageId; let i = $index) {
              <div class="fm-scroll-page" [id]="'fm-preview-page-' + i">
                <ng-container
                  [ngTemplateOutlet]="pageContent"
                  [ngTemplateOutletContext]="{ $implicit: page, index: i }"
                ></ng-container>
              </div>
            }
          </div>

          <div class="fm-preview-pagination">
            <button
              type="button"
              class="fm-preview-page-btn fm-preview-page-arrow"
              [disabled]="!canGoPrev()"
              (click)="goPrev()"
              aria-label="Previous page"
            >
              &#8249;
            </button>
            @for (item of pageNumbers(); track $index) {
              @if (item === '…') {
                <span class="fm-preview-page-ellipsis">&hellip;</span>
              } @else {
                <button
                  type="button"
                  class="fm-preview-page-btn"
                  [class.active]="item === activePageNumber()"
                  (click)="goToPage(item)"
                >
                  {{ item }}
                </button>
              }
            }
            <button
              type="button"
              class="fm-preview-page-btn fm-preview-page-arrow"
              [disabled]="!canGoNext()"
              (click)="goNext()"
              aria-label="Next page"
            >
              &#8250;
            </button>
          </div>
        </div>
      }
    </div>
  </div>
</div>

<ng-template #ornament>
  <svg
    class="fm-page-ornament-svg"
    width="72"
    height="14"
    viewBox="0 0 72 14"
    fill="none"
    aria-hidden="true"
  >
    <path d="M2 7 H70" stroke="currentColor" stroke-width="1" />
    <path
      d="M36 7 C33 3, 28 3, 26 7 C28 11, 33 11, 36 7 Z"
      fill="currentColor"
    />
    <path
      d="M20 7 C18 4.5, 15 4.5, 13.5 7 C15 9.5, 18 9.5, 20 7 Z"
      fill="currentColor"
    />
    <path
      d="M52 7 C50 4.5, 47 4.5, 45.5 7 C47 9.5, 50 9.5, 52 7 Z"
      fill="currentColor"
    />
  </svg>
</ng-template>

<ng-template #pageContent let-page let-index="index">
  <div class="fm-page-inner" [class.fm-page-inner-fill]="index % 2 !== 0">
    @if (index % 2 === 0) {
      <!-- Narrative page: chapter opener — eyebrow, title, tagline, photo, then body copy -->
      <p class="fm-page-eyebrow">Chapter {{ index / 2 + 1 }}</p>
      <span class="fm-page-ornament"><ng-container [ngTemplateOutlet]="ornament" /></span>
      <h2 class="fm-preview-page-title">{{ page.title }}</h2>
      <p class="fm-page-tagline">{{ firstSentence(page.description) }}</p>

      @if (page.media?.length) {
        <div class="fm-page-image-wrap fm-page-image-plain">
          <img [src]="page.media[0].mediaUrl" class="fm-page-image" alt="" />
        </div>
      }

      @if (remainingText(page.description)) {
        <p class="fm-page-text fm-page-dropcap">{{ remainingText(page.description) }}</p>
      }
    } @else {
      <!-- Quote page: framed photo first, then the pull-quote, then a QR card for the story -->
      <div class="fm-page-group">
        @if (page.media?.length) {
          <div class="fm-page-image-wrap fm-page-image-framed">
            <img [src]="page.media[0].mediaUrl" class="fm-page-image" alt="" />
          </div>
        }

        <p class="fm-page-quote">{{ page.description }}</p>
        <span class="fm-page-heart">&#9825;</span>
      </div>

      @if (page.qrCodeUrl) {
        <div class="fm-page-qr-card">
          <img [src]="page.qrCodeUrl" class="fm-page-qr-image" alt="Scan to watch this memory" />
          <div class="fm-page-qr-text">
            <p class="fm-page-qr-caption mb-0">Scan to watch this memory</p>
            <p class="fm-page-qr-desc mb-0">{{ page.title }}</p>
          </div>
          <button type="button" class="fm-page-qr-play" aria-label="Play">
            <img src="images/icons/fm-play.svg" class="fm-svg" alt="" />
          </button>
        </div>
      }
    }

    <span class="fm-page-number">&#8594; {{ index + 1 }} &#8592;</span>
  </div>
</ng-template>
